<template>
    <div>
        <v-container class="ma-n3">
            <v-row no-gutters align='center' justify='space-between' :class="{'collapsed-heading': !expanded}" class='mr-n6'>
                <h2 class="clickable" @click='expanded=!expanded'>
                    Additional Warranty
                    <v-icon v-if='expanded'>arrow_drop_up</v-icon>
                    <v-icon v-else>arrow_drop_down</v-icon>
                </h2>
            </v-row>
        </v-container>
        <v-expand-transition>
            <v-card v-show='expanded'>
            <v-data-table
                :items="this.customWarranties"
                :items-per-page="options.itemsPerPage"
                :headers="headers"
                :loading="$wait.is(['loading custom warranties'])"
                :footer-props.sync="footerProps"
                :options.sync="options"
            >
                <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                <template v-slot:item="items">
                    <tr>
                        <td>{{ items.item.description }}</td>
                        <td @click="openDefectNoticeEdit(items.item.defectNoticeId)">{{ items.item.ednNumber }}</td>
                        <td>{{ formattedStartDate(items.item) }}</td>
                        <td>{{ items.item.length }}</td>
                        <td>{{ items.item.warrantyInterval }}</td>
                        <td v-if="!unit.isDeleted && userIsFleetOrWorkshopManager">
                            <v-layout>
                                <v-btn icon small @click="openEdit(items.item)">
                                    <v-icon>edit</v-icon>
                                </v-btn>
                                <v-btn @click="confirmDelete(items.item)" icon small color="red">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </v-layout>
                        </td>
                    </tr>
                </template>
            </v-data-table>
            <v-card-actions v-if="!disabled && userIsFleetOrWorkshopManager">
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="openAdd()">Add Warranty</v-btn>
                <v-dialog v-model="dialog" max-width="500px" persistent>
                    <custom-warranty-form ref="warrantyform" v-model="model" :garageLocationId="unit.garageLocationId" @add="addWarranty($event.id, $event.model)" @edit="updateWarranty($event.id, $event.model)" @close="dialog = false">
                        <template v-slot:header>
                            <v-spacer></v-spacer>
                            <v-btn @click="dialog = false" icon text>
                                <v-icon>close</v-icon>
                            </v-btn>
                        </template>
                    </custom-warranty-form>
                </v-dialog>
            </v-card-actions>
        </v-card>
        </v-expand-transition>
    </div>
</template>

<script>
import { DateFormat } from '@/plugins/date-filter';
import CustomWarrantyForm from '@/components/Units/Details/CustomWarranty/CustomWarrantyForm.vue';
import CustomWarrantyClient from '@/components/Units/Details/CustomWarranty/CustomWarrantyClient.vue';
import { Event } from '@/plugins/bus';
import moment from 'moment';

export default {
    props: {
        unit: {
            type: Object,
            required: true
        }
    },
    mixins: [CustomWarrantyClient],
    components: {
        CustomWarrantyForm
    },
    data() {
        return {
            dialog: false,
            model: {},
            dateFormat: DateFormat.DefaultDisplay,
            expanded: false,
            customWarranties: [],
            options: {
                itemsPerPage: 10,
                page: 1,
                sortBy: ['startDate'],
                sortDesc: [true]
            },
            footerProps: {
                itemsPerPageOptions: [10, 50, 100]
            },
            headers: [
                { text: 'Description', value: 'description', sortable: true },
                { text: 'EDN', value: 'ednNumber', sortable: true },
                { text: 'Commencement', value: 'startDate', sortable: true },
                { text: 'Length', value: 'length', sortable: true },
                { text: 'Interval', value: 'warrantInterval', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ],
            totalItems: 0
        };
    },
    computed: {
        unitId() {
            return this.$route.params.unitId;
        },
        disabled() {
            return false;
        }
    },
    watch: {
        async unitId() {
            this.customWarranties = await this.getCustomWarranties();
        },
        options: {
            async handler() {
                this.customWarranties = await this.getCustomWarranties();
            },
            deep: true
        }
    },
    methods: {
        openAdd() {
            if (this.$refs.warrantyform) {
                this.$refs.warrantyform.resetForm();
            }
            this.model = {
                unitId: this.unitId
            };
            this.dialog = true;
        },
        openEdit(item) {
            if (this.$refs.warrantyform) {
                this.$refs.warrantyform.resetForm(item.ednNumber);
            }
            this.model = {
                ...item,
                unitId: this.unitId,
                initialDate: item.startDate
            };
            this.dialog = true;
        },
        confirmDelete(customWarranty) {
            this.$confirmCancel.show(`Delete Additional Warranty?`, `<p>This will permanently delete this additional warranty</p>`, 'Delete it');

            const deleteWarranty = confirmed => {
                this.$confirmCancel.$off('confirm-cancel', deleteWarranty);
                if (!confirmed) {
                    return;
                }

                this.deleteWarranty(customWarranty.id);
            };
            this.$confirmCancel.$on('confirm-cancel', deleteWarranty);
        },
        async addWarranty(id, model) {
            await this.addCustomWarranty(id, model);
        },
        async updateWarranty(id, model) {
            await this.updateCustomWarranty(id, model);
        },
        async deleteWarranty(id) {
            await this.deleteCustomWarranty(id);
        },
        async handleCustomWarrantyUpdated() {
            this.dialog = false;

            this.customWarranties = await this.getCustomWarranties();
        },
        async handleCustomWarrantyDeleted() {
            this.customWarranties = await this.getCustomWarranties();
        },
        formattedStartDate(item) {
            return item.startDate ? moment(item.startDate).format(DateFormat.DefaultDisplay) : '';
        },
        openDefectNoticeEdit(defectNoticeId) {
          this.$bus.$emit(Event.OpenDefectNoticeClicked, defectNoticeId);
        }
    },
    async mounted() {
        this.customWarranties = await this.getCustomWarranties();

        this.$bus.$on(Event.CustomWarrantyUpdated, this.handleCustomWarrantyUpdated);
        this.$bus.$on(Event.CustomWarrantyDeleted, this.handleCustomWarrantyDeleted);
    },
    destroyed() {
        this.$bus.$off(Event.CustomWarrantyUpdated, this.handleCustomWarrantyUpdated);
        this.$bus.$off(Event.CustomWarrantyDeleted, this.handleCustomWarrantyDeleted);
    }
};
</script>

<style>

</style>
